import * as React from 'react'
import styled, { AnyStyledComponent } from 'styled-components'
import moment from 'moment'

import * as constants from '../../../static/constants'
import * as interfaces from '../../../static/interfaces'
import * as Hooks from '../../../utils/hooks'
import Typography from 'components/atoms/_layouts/Typography'
import { ReservationForm } from './parts/ReservationForm'
import { ISelectedGuests } from 'utils/reservation'

import { ReservationSelectedDatesDate } from './parts/atoms/ReservationSelectedDatesDate'
import { PrivateGroupOption } from './parts/atoms/PrivateGroupOption'
import { ReservationDetailInfo } from './parts/ReservationDetailInfo'
import RefundInfoModal from './parts/RefundInfoModal'

import { postService } from '../../../core/services'
import { FullScreenReservationLayout } from '../_layouts/fullscreenReservationLayout'
import {
  ReservationFullScreenModalHeaderContent,
  ReservationModalType,
} from 'components/molecules/Reservation/ReservationFullScreenModalHeader'
import { IJsonResponse } from 'core/JsonApiSerializer'
import {
  clearReservationStorageAndInfo,
  getReservationStorageData,
  updateReservationStorageData,
} from 'utils/reservationSessionStorage'

declare var window: interfaces.IWindow

interface IWrapProps {
  user: interfaces.IUser
  countries: any
  isSignedIn: boolean
  post: IJsonResponse
  isOwner: boolean
  cancel_policy: string
}

interface IProps {
  post: any
  memberPost: any
  guests: ISelectedGuests
  reservation: {
    selectedDate: moment.Moment
    totalPrice: number
    departure: any
  }
  cancelPolicy: string
  guest: interfaces.IUser
  countries: any
  isSignedIn: boolean
}
const getInitialPeopleCount = (guests: ISelectedGuests, type) =>
  type === 'private'
    ? { num_of_adults: guests ? guests.adults + guests.children + guests.infants : 1 }
    : {
        num_of_adults: guests ? guests.adults : 1,
        num_of_children: guests ? guests.children : 0,
        num_of_infants: guests ? guests.infants : 0,
      }

const WrapReservationNew: React.FC<IWrapProps> = props => {
  const { post } = postService.getPostFromJson(props.post)

  const reservationStorageData = getReservationStorageData()

  React.useEffect(() => {
    if (!reservationStorageData?.memberPost) {
      clearReservationStorageAndInfo()
      setTimeout(() => {
        window.location.href = `/posts/${post.slug}`
      }, 300)
    }
  }, [])

  if (!reservationStorageData?.memberPost) {
    return null
  }

  return (
    <ReservationNew
      {...props}
      cancelPolicy={props.cancel_policy}
      guest={props.user}
      post={post}
      guests={reservationStorageData?.initParams?.guests || reservationStorageData?.guests}
      memberPost={reservationStorageData?.memberPost}
      reservation={{
        ...reservationStorageData?.reservation,
        selectedDate: moment(reservationStorageData?.reservation?.selectedDate),
        totalPrice: reservationStorageData?.reservation?.totalPrice ?? 0,
        departure: reservationStorageData?.reservation?.departure ?? null,
      }}
    />
  )
}

const ReservationNew: React.FC<IProps> = props => {
  Hooks.SetDocumentTitle(props.post.name)
  const [isCompleted, setIsCompleted] = React.useState(false)
  const [createdReservation, setCreatedReservation] = React.useState(null)
  const [showRefundInfoModal, setShowRefundInfoModal] = React.useState(false)
  const [chargePrice, setChargePrice] = React.useState(0)
  const [hasCoupon, setHasCoupon] = React.useState<boolean>(false)
  const [guests, setGuests] = React.useState(
    getInitialPeopleCount(props.guests, props.post?.post_type)
  )
  const modalLayoutRef = React.useRef(null)
  const getParticipantTypePrice = (type: string): number => {
    const adultPrice =
      props.memberPost.post_prices.find(
        ({ participant_type, pricing_type }) =>
          participant_type === 'adult' || pricing_type === 'private'
      )?.price ?? 0

    const item = props.memberPost?.post_prices.find(
      ({ participant_type }) => participant_type === type
    )

    return item ? item?.price : adultPrice
  }

  const scrollToTop = React.useCallback(() => {
    if (modalLayoutRef.current) {
      modalLayoutRef.current.scroll({
        top: 0,
        behavior: 'instant',
      })
    }
  }, [modalLayoutRef])

  const getTotalParticipantTypePrice = (quantity: number = 0, price: number) => {
    if (quantity === 0) {
      return 0
    }
    const total = quantity * price

    if (isNaN(total)) {
      return ''
    }

    return total
  }

  React.useEffect(() => {
    if (isCompleted) {
      const formattedGuests = {
        adults: guests.num_of_adults,
        children: guests.num_of_children ?? 0,
        infants: guests.num_of_infants ?? 0,
      }

      updateReservationStorageData({
        chargePrice,
        guests: formattedGuests,
        hasCoupon: hasCoupon || !!createdReservation?.coupon_name,
        createdReservation,
        memberPost: props.memberPost,
      })
      window.location.href = `/posts/${props?.post?.slug}/reservations/result`
    }
    scrollToTop()
  }, [isCompleted])

  return (
    <FullScreenReservationLayout
      headerContent={
        <ReservationFullScreenModalHeaderContent
          type={ReservationModalType.New}
          closeHandle={() => {
            window.history.back()
          }}
        />
      }
    >
      <ReservationNewWrapper>
        {showRefundInfoModal && (
          <RefundInfoModal
            cancelPolicy={props.cancelPolicy}
            onClose={() => setShowRefundInfoModal(false)}
          />
        )}

        <div className="colLeft">
          <S.Reservation>
            <div className="title">
              <Typography type="headline" size="md">
                Booking Summary
              </Typography>
            </div>
            {!props.post.instant_booking && (
              <div className="boxItem onRequestMessage">
                <Typography type="subtitle" size="xl">
                  Confirmation
                </Typography>
                <Typography type="body" size="lg">
                  If you don't receive a confirmation email, reach out to our customer support.
                </Typography>
              </div>
            )}
            <div className="boxItem">
              <Typography type="subtitle" size="xl">
                Date
              </Typography>
              <ReservationSelectedDatesDate
                date={{ date: props.reservation.selectedDate }}
                startTime={props.reservation.departure}
                post={props.memberPost}
                fontSize={20}
              />
            </div>
            <PrivateGroupOption />

            <ReservationForm
              isSignedIn={props.isSignedIn}
              countries={props.countries}
              post={props.memberPost}
              guest={props.guest}
              guests={guests}
              setGuests={setGuests}
              chargePrice={chargePrice}
              setChargePrice={setChargePrice}
              setHasCoupon={setHasCoupon}
              reservation={props.reservation}
              setCreatedReservation={setCreatedReservation}
              getParticipantTypePrice={getParticipantTypePrice}
              setIsCompleted={setIsCompleted}
            />
          </S.Reservation>
        </div>
        <div className="colRight">
          <ReservationDetailInfo
            post={props.post}
            memberPost={props.memberPost}
            isPostInFamily={props.post.family_owner}
            cancelPolicy={props.cancelPolicy}
            chargePrice={chargePrice}
            guests={{
              adults: guests.num_of_adults,
              children: guests.num_of_children,
              infants: guests.num_of_infants,
            }}
            hasCoupon={hasCoupon || !!createdReservation?.coupon_name}
            createdReservation={createdReservation}
            getParticipantTypePrice={getParticipantTypePrice}
            getTotalParticipantTypePrice={getTotalParticipantTypePrice}
            setShowRefundInfoModal={setShowRefundInfoModal}
          />
        </div>
      </ReservationNewWrapper>
    </FullScreenReservationLayout>
  )
}
const S: { [key: string]: AnyStyledComponent } = {}

S.Reservation = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding-bottom: 12px;
  margin: 0;
  @media screen and (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
    gap: 1.6rem;
  }
  .back,
  .title {
    font-size: 16px;
  }
  .date {
    display: inline-flex;
    flex-direction: column;
    gap: 12px;
  }
  .right {
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
  }
  .onRequestMessage {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
  }
  .boxItem > div:last-child {
    @media screen and (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      padding: 1.2rem 0;
    }
  }
`
const ReservationNewWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 8rem;
  width: 100%;
  height: max-content;
  @media screen and (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
    flex-direction: column-reverse;
    gap: 4rem;
    &.completed {
      flex-direction: column;
      gap: 0;
      .colLeft {
        padding: 3.2rem 0 2.4rem;
      }
      .colRight {
        padding: 0 0 4rem;
      }
    }
  }
  @media screen and (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
    gap: 2.4rem;
  }

  .colLeft {
    width: 56rem;
    padding: 4.8rem 0 8rem;

    @media screen and (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
      width: 100%;
      max-width: 56rem;
      padding: 0 0 4rem;
      margin: 0 auto;
    }
  }

  .colRight {
    width: 48rem;
    min-height: 100%;
    padding: 4rem 0 6.4rem;
    @media screen and (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
      width: 100%;
      max-width: 56rem;
      padding: 2.4rem 0 0;
      margin: 0 auto;
    }
    @media screen and (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      padding: 1.6rem 0 0;
    }
  }

  .ReservationNew_Post {
    display: flex;
    align-items: center;
    @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      display: block;
    }
  }
`

export default WrapReservationNew
